import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import {
  CalendarDaysIcon, PlusIcon, TrashIcon, XMarkIcon,
  ClockIcon, NoSymbolIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';
import { DATE_FLOOR } from '../../lib/dateBounds';

function fmtDateTime(s) {
  return new Date(s).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fmtDateLabel(s) {
  return new Date(s).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

function groupByDay(slots) {
  const days = {};
  for (const s of slots) {
    const dayKey = new Date(s.start_at).toISOString().slice(0, 10);
    if (!days[dayKey]) days[dayKey] = [];
    days[dayKey].push(s);
  }
  return Object.entries(days)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => ({ key, items }));
}

export default function AdminInterviewSlotsPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/interview-slots');
      setSlots(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load slots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancelSlot = async (id) => {
    if (!window.confirm('Cancel this slot? It will be hidden from new candidates but any existing bookings stay reachable for rescheduling.')) return;
    try {
      await api.patch(`/interview-slots/${id}`, { cancelled: true });
      toast.success('Slot cancelled');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel');
    }
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Delete this slot? (Only allowed if nothing is booked.)')) return;
    try {
      await api.delete(`/interview-slots/${id}`);
      toast.success('Slot deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const days = groupByDay(slots);

  return (
    <div className="animate-slide-up">
      <Toaster position="top-right" />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Interview Slots</h1>
          <p className="text-gray-500 text-sm">Publish open times that candidates can pick from after they finish their questionnaire.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAdd(true)}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium px-4 py-2 rounded-xl text-sm inline-flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" /> Single slot
          </button>
          <button onClick={() => setShowBulk(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl text-sm inline-flex items-center gap-1.5">
            <PlusIcon className="w-4 h-4" /> Add bulk slots
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : days.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <CalendarDaysIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">No interview slots posted</p>
          <p className="text-gray-500 text-sm">Add a few times and candidates can self-schedule after their questionnaire.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {days.map(({ key, items }) => (
            <div key={key} className="bg-white border border-gray-200 rounded-2xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CalendarDaysIcon className="w-5 h-5 text-orange-500" />
                {fmtDateLabel(items[0].start_at)}
              </h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-100">
                    <th className="pb-2 font-medium">Time</th>
                    <th className="pb-2 font-medium">Duration</th>
                    <th className="pb-2 font-medium">Booked</th>
                    <th className="pb-2 font-medium">Notes</th>
                    <th className="pb-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(s => {
                    const full = s.booked_count >= s.capacity;
                    return (
                      <tr key={s.id} className={`border-b border-gray-50 ${s.cancelled ? 'opacity-50' : ''}`}>
                        <td className="py-2.5 text-gray-900 font-medium flex items-center gap-1.5">
                          <ClockIcon className="w-4 h-4 text-gray-400" />
                          {new Date(s.start_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </td>
                        <td className="py-2.5 text-gray-600">{s.duration_minutes} min</td>
                        <td className="py-2.5">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            s.cancelled
                              ? 'bg-gray-100 text-gray-500'
                              : full
                                ? 'bg-amber-100 text-amber-700'
                                : s.booked_count > 0
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-blue-100 text-blue-700'
                          }`}>
                            {s.cancelled ? 'Cancelled' : `${s.booked_count}/${s.capacity}`}
                          </span>
                        </td>
                        <td className="py-2.5 text-gray-600 text-xs">
                          {s.location_notes || s.zoom_link || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="py-2.5 text-right">
                          <div className="flex justify-end gap-1">
                            {!s.cancelled && (
                              <button onClick={() => cancelSlot(s.id)} title="Cancel slot"
                                className="text-gray-500 hover:text-amber-600 p-1">
                                <NoSymbolIcon className="w-4 h-4" />
                              </button>
                            )}
                            <button onClick={() => deleteSlot(s.id)} title="Delete slot"
                              className="text-gray-500 hover:text-red-600 p-1">
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddSlotModal onClose={() => setShowAdd(false)} onSaved={load} />}
      {showBulk && <BulkSlotModal onClose={() => setShowBulk(false)} onSaved={load} />}
    </div>
  );
}

function AddSlotModal({ onClose, onSaved }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { duration_minutes: 30, capacity: 1 },
  });
  const [saving, setSaving] = useState(false);

  const submit = async (data) => {
    setSaving(true);
    try {
      await api.post('/interview-slots', {
        start_at: data.start_at,
        duration_minutes: parseInt(data.duration_minutes, 10),
        capacity: parseInt(data.capacity, 10),
        location_notes: data.location_notes || null,
        zoom_link: data.zoom_link || null,
      });
      toast.success('Slot added');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add slot');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold">Add a single slot</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit(submit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date &amp; time *</label>
            <input type="datetime-local" {...register('start_at', { required: true })}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            {errors.start_at && <p className="text-red-500 text-xs mt-1">Required</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
              <input type="number" min="5" max="240" {...register('duration_minutes')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
              <input type="number" min="1" max="20" {...register('capacity')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zoom / meeting link</label>
            <input {...register('zoom_link')} placeholder="https://zoom.us/j/..."
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location / notes</label>
            <input {...register('location_notes')} placeholder="In-person at BBC main office"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving…' : 'Add slot'}
            </button>
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkSlotModal({ onClose, onSaved }) {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { duration_minutes: 30, capacity: 1, start_time: '10:00', end_time: '14:00' },
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (data) => {
    setSaving(true);
    try {
      const { data: r } = await api.post('/interview-slots/bulk', {
        start_date: data.start_date,
        start_time: data.start_time,
        end_time: data.end_time,
        duration_minutes: parseInt(data.duration_minutes, 10),
        capacity: parseInt(data.capacity, 10),
        location_notes: data.location_notes || null,
        zoom_link: data.zoom_link || null,
      });
      setResult(r);
      toast.success(`${r.created} slots added`);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add slots');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold">Add a block of slots</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        {result ? (
          <div className="p-6 text-center">
            <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="font-medium text-gray-900">Added {result.created} slot{result.created === 1 ? '' : 's'}</p>
            <p className="text-sm text-gray-500 mt-1">They're live in the candidate self-schedule list.</p>
            <button onClick={onClose} className="mt-5 px-4 py-2 bg-gray-100 rounded-xl text-gray-700 hover:bg-gray-200 text-sm font-medium">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(submit)} className="p-6 space-y-4">
            <p className="text-xs text-gray-500">Generates back-to-back slots within the window.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input type="date" min={DATE_FLOOR} {...register('start_date', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              {errors.start_date && <p className="text-red-500 text-xs mt-1">Required</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
                <input type="time" {...register('start_time', { required: true })}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Until *</label>
                <input type="time" {...register('end_time', { required: true })}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slot length (min)</label>
                <input type="number" min="5" max="240" {...register('duration_minutes')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Capacity</label>
                <input type="number" min="1" max="20" {...register('capacity')}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zoom / meeting link</label>
              <input {...register('zoom_link')} placeholder="https://zoom.us/j/..."
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location / notes</label>
              <input {...register('location_notes')} placeholder="In-person at BBC main office"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="flex-1 bg-orange-500 text-white font-semibold py-2.5 rounded-xl hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Generating…' : 'Generate slots'}
              </button>
              <button type="button" onClick={onClose}
                className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
